/**
 * Structured Logger
 * Provides consistent, JSON-structured logging for the frontend application.
 * Integrates correlation IDs for end-to-end request tracing.
 */

import { generateCorrelationId } from '@/lib/api-client';
import { env, isProd } from '@/config/env';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  correlationId?: string;
  context?: string;
  data?: Record<string, unknown>;
  error?: { message: string; stack?: string };
  userId?: string;
  tenantId?: string;
}

const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

// In production, suppress debug; in dev, show all
const MIN_LEVEL: LogLevel = isProd ? 'info' : 'debug';

function shouldLog(level: LogLevel): boolean {
  return LOG_LEVEL_PRIORITY[level] >= LOG_LEVEL_PRIORITY[MIN_LEVEL];
}

function formatEntry(entry: LogEntry): string {
  if (isProd) {
    // JSON format for structured log aggregation
    return JSON.stringify(entry);
  }
  // Human-readable in dev
  const prefix = `[${entry.level.toUpperCase()}]`;
  const ctx = entry.context ? `[${entry.context}]` : '';
  const cid = entry.correlationId ? `(${entry.correlationId})` : '';
  return `${prefix}${ctx}${cid} ${entry.message}`;
}

function emit(entry: LogEntry): void {
  if (!shouldLog(entry.level)) return;

  const formatted = formatEntry(entry);

  switch (entry.level) {
    case 'debug':
      console.debug(formatted, entry.data ?? '');
      break;
    case 'info':
      console.info(formatted, entry.data ?? '');
      break;
    case 'warn':
      console.warn(formatted, entry.data ?? '');
      break;
    case 'error':
      console.error(formatted, entry.error ?? entry.data ?? '');
      break;
  }
}

/**
 * Create a scoped logger instance with a fixed context name.
 */
export function createLogger(context: string) {
  let _correlationId: string | undefined;
  let _userId: string | undefined;
  let _tenantId: string | undefined;

  const logger = {
    /** Set a correlation ID for all subsequent log calls */
    setCorrelationId(id: string) {
      _correlationId = id;
      return logger;
    },

    /** Set user context for all subsequent log calls */
    setUserContext(userId: string, tenantId?: string) {
      _userId = userId;
      _tenantId = tenantId;
      return logger;
    },

    /** Generate and set a new correlation ID, returning it */
    newCorrelation(): string {
      _correlationId = generateCorrelationId();
      return _correlationId;
    },

    debug(message: string, data?: Record<string, unknown>) {
      emit({ level: 'debug', message, timestamp: new Date().toISOString(), context, correlationId: _correlationId, data, userId: _userId, tenantId: _tenantId });
    },

    info(message: string, data?: Record<string, unknown>) {
      emit({ level: 'info', message, timestamp: new Date().toISOString(), context, correlationId: _correlationId, data, userId: _userId, tenantId: _tenantId });
    },

    warn(message: string, data?: Record<string, unknown>) {
      emit({ level: 'warn', message, timestamp: new Date().toISOString(), context, correlationId: _correlationId, data, userId: _userId, tenantId: _tenantId });
    },

    error(message: string, err?: unknown, data?: Record<string, unknown>) {
      const errorInfo = err instanceof Error
        ? { message: err.message, stack: isProd ? undefined : err.stack }
        : err
          ? { message: String(err) }
          : undefined;
      emit({ level: 'error', message, timestamp: new Date().toISOString(), context, correlationId: _correlationId, error: errorInfo, data, userId: _userId, tenantId: _tenantId });
    },
  };

  return logger;
}

/**
 * Global application logger for general use.
 */
export const appLogger = createLogger('app');
